import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useProfileStore, getFacilityKey, getPatientKey } from "@/store/profile";
import patientRequest from "@/api/patient-request";
import {
  type TaskItem,
  isStatementTask,
  isInfoTask,
  isFormTask,
  isFvTask,
  isInsuranceTask,
} from "@/types/tasks";
import { openPortalTask } from "@/utils/openPortalTask";

export interface DashTaskProps {
  onTaskCountChange?: (count: number) => void;
}

function TaskCard({
  title,
  description,
  isLoading,
  disabled,
  onOpen,
}: {
  title: string;
  description: string;
  isLoading: boolean;
  disabled: boolean;
  onOpen: () => void;
}) {
  return (
    <li
      className="flex overflow-hidden rounded-lg border border-healthsource-lightLavender bg-white transition-shadow hover:shadow-md"
      style={{ boxShadow: "0 2px 8px rgba(30,37,43,0.06)" }}
    >
      <div
        className="w-1 flex-shrink-0 rounded-r-sm bg-healthsource-navy"
      />
      <div className="min-w-0 flex-1 px-4 py-4 sm:pl-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="font-extrabold text-[15px] text-healthsource-dark">
              {title}
            </p>
            <p className="mt-1 text-[13px] text-healthsource-gray leading-snug">
              {description}
            </p>
          </div>
          <button
            type="button"
            disabled={disabled}
            onClick={onOpen}
            className="min-h-11 flex-shrink-0 rounded-md bg-healthsource-red px-4 py-2 text-[13px] font-bold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoading ? "…" : "Open"}
          </button>
        </div>
      </div>
    </li>
  );
}

export function DashTask({ onTaskCountChange }: DashTaskProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPatient = useProfileStore((s) => s.currentPatient);

  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState<number | null>(null);

  const facilityKey = getFacilityKey();
  const patientKey = getPatientKey();
  const statementCount = tasks.filter((t) => isStatementTask(t.taskType)).length;
  const infoCount = tasks.filter((t) => isInfoTask(t)).length;
  const formCount = tasks.filter((t) => isFormTask(t.taskType)).length;
  const fvCount = tasks.filter((t) => isFvTask(t.taskType)).length;
  const insuranceCount = tasks.filter((t) => isInsuranceTask(t)).length;
  const filteredCount =
    (statementCount > 0 ? 1 : 0) +
    (infoCount > 0 ? 1 : 0) +
    (insuranceCount > 0 ? 1 : 0) +
    (formCount > 0 ? 1 : 0) +
    (fvCount > 0 ? 1 : 0);

  useEffect(() => {
    onTaskCountChange?.(filteredCount);
  }, [filteredCount, onTaskCountChange]);

  useEffect(() => {
    if (!currentPatient || location.pathname !== "/") return;
    let cancelled = false;
    setLoading(true);
    patientRequest.request
      .gettasks(facilityKey, patientKey)
      .then((res) => {
        if (!cancelled) setTasks((res?.data as TaskItem[]) ?? []);
      })
      .catch(() => {
        if (!cancelled) setTasks([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentPatient, location.pathname, facilityKey, patientKey]);

  const handleTask = async (task: TaskItem) => {
    if (!currentPatient || actionLoading) return;
    setActionLoading(true);
    setCurrentTaskId(task.taskID);
    try {
      await openPortalTask({ navigate, facilityKey, patientKey, task, allTasks: tasks });
    } catch (err) {
      console.error("Task action error:", err);
    } finally {
      setActionLoading(false);
      setCurrentTaskId(null);
    }
  };

  if (!currentPatient) return null;

  const statementTasks = tasks.filter((t) => isStatementTask(t.taskType));
  const infoTasks = tasks.filter((t) => isInfoTask(t));
  const formTasks = tasks.filter((t) => isFormTask(t.taskType));
  const fvTasks = tasks.filter((t) => isFvTask(t.taskType));
  const insuranceTasks = tasks.filter((t) => isInsuranceTask(t));

  const firstStatement = statementTasks[0];
  const firstInfo = infoTasks[0];
  const firstForm = formTasks[0];
  const firstFv = fvTasks[0];
  const firstInsurance = insuranceTasks[0];

  const visibleCount =
    (statementTasks.length > 0 ? 1 : 0) +
    (infoTasks.length > 0 ? 1 : 0) +
    (insuranceTasks.length > 0 ? 1 : 0) +
    (formTasks.length > 0 ? 1 : 0) +
    (fvTasks.length > 0 ? 1 : 0);

  return (
    <>
      {loading && (
        <div className="py-6 text-center text-sm text-healthsource-gray">
          Loading tasks…
        </div>
      )}
      {!loading && visibleCount === 0 && (
        <div className="py-6 text-center text-sm text-healthsource-gray">
          No tasks found.
        </div>
      )}
      {!loading && visibleCount > 0 && (
        <ul className="flex flex-col gap-3 sm:gap-3.5 sm:p-5">
          {statementTasks.length > 0 && firstStatement && (
            <TaskCard
              title="Pay Statement / Balance"
              description="View your statement and pay your balance."
              isLoading={
                actionLoading && currentTaskId === firstStatement.taskID
              }
              disabled={actionLoading}
              onOpen={() => handleTask(firstStatement)}
            />
          )}
          {infoTasks.length > 0 && firstInfo && (
            <TaskCard
              title="Complete Information"
              description={
                infoTasks.length > 1
                  ? "Complete your health history and information (multiple items)."
                  : "Complete your health history, demographics, or insurance information."
              }
              isLoading={actionLoading && currentTaskId === firstInfo.taskID}
              disabled={actionLoading}
              onOpen={() => handleTask(firstInfo)}
            />
          )}
          {insuranceTasks.length > 0 && firstInsurance && (
            <TaskCard
              title="Insurance Information"
              description="Tell us about your health insurance and add photos of your insurance card."
              isLoading={actionLoading && currentTaskId === firstInsurance.taskID}
              disabled={actionLoading}
              onOpen={() => handleTask(firstInsurance)}
            />
          )}
          {formTasks.length > 0 && firstForm && (
            <TaskCard
              title="Complete and Sign Forms"
              description={
                formTasks.length > 1
                  ? `${formTasks.length} form(s) need your signature. Forms link to each other.`
                  : "One form needs your signature."
              }
              isLoading={actionLoading && currentTaskId === firstForm.taskID}
              disabled={actionLoading}
              onOpen={() => handleTask(firstForm)}
            />
          )}
          {fvTasks.length > 0 && firstFv && (
            <TaskCard
              title="Review files"
              description="Open files linked to your account."
              isLoading={actionLoading && currentTaskId === firstFv.taskID}
              disabled={actionLoading}
              onOpen={() => handleTask(firstFv)}
            />
          )}
        </ul>
      )}
    </>
  );
}
